/**
 * <EmployeesReissueInviteDialog> — re-issue invite for a PENDING user.
 *
 * Phase-2 Wave-4 W4.T4. AlertDialog with two states:
 *
 *   1. Confirm: «Перевыпустить приглашение для <ФИО>?» + warning that
 *      the OLD link becomes invalid immediately.
 *   2. Reveal:  the freshly-minted URL via <TokenLinkDisplay> with
 *      24h countdown.
 *
 * Routes through `useReissueInviteMutation` from `@/api/users` (the
 * factory is already wired to invalidate qk.users.all on success). The
 * Pending-tab row's `updatedAt` shifts so the table re-orders.
 *
 * 409 NOT_PENDING: the user is no longer pending (they already accepted
 * the invite OR were archived). We toast a hint suggesting the actor
 * issue a password reset instead (research-B G11).
 */
import * as React from 'react';

import type { UserDetail } from '@aris/shared';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { rightsRu } from '@/i18n';
import { formatDate } from '@/lib/date';
import { ApiError } from '@/lib/fetcher';

import { useReissueInviteMutation } from '@/api/users';

import { TokenLinkDisplay } from './token-link-display';

export interface EmployeesReissueInviteDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  user: UserDetail | null;
}

interface RevealState {
  inviteUrl: string;
  expiresAt: string;
}

export function EmployeesReissueInviteDialog(
  props: EmployeesReissueInviteDialogProps,
): React.ReactElement | null {
  const { open, onOpenChange, user } = props;
  const reissueMutation = useReissueInviteMutation();

  const [reveal, setReveal] = React.useState<RevealState | null>(null);
  // Track whether the current open cycle produced a successful reveal —
  // closing AFTER a reveal must fire the "Приглашение перевыпущено" toast
  // (R6.7 BUG-3 spec), but closing BEFORE the reveal (Cancel) must not.
  const revealedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      // Only emit the post-close toast if a reveal actually happened in
      // this open cycle. Resetting state happens after the effect runs so
      // the dialog content unmounts cleanly.
      if (revealedRef.current) {
        toast('Приглашение перевыпущено', { variant: 'success' });
        revealedRef.current = false;
      }
      setReveal(null);
    }
  }, [open]);

  const onConfirm = React.useCallback(async () => {
    if (!user) return;
    try {
      const result = await reissueMutation.mutateAsync({ id: user.id });
      revealedRef.current = true;
      setReveal({ inviteUrl: result.inviteUrl, expiresAt: result.expiresAt });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.code === 'NOT_PENDING') {
          toast(
            'Пользователь уже активирован. Используйте «Сбросить пароль», если нужен новый доступ.',
            { variant: 'warning' },
          );
          onOpenChange(false);
          return;
        }
        toast(err.message, { variant: 'error' });
        return;
      }
      toast('Не удалось перевыпустить приглашение.', { variant: 'error' });
    }
  }, [reissueMutation, onOpenChange, user]);

  if (!user) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="employees-reissue-dialog">
        {reveal === null ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle data-testid="employees-reissue-title">
                Перевыпустить приглашение для {user.fullName}?
              </AlertDialogTitle>
              <AlertDialogDescription data-testid="employees-reissue-body">
                Старая ссылка станет недействительной немедленно. Будет создана новая ссылка со
                сроком действия 24 часа.
              </AlertDialogDescription>
              {/* Ф8 · В-Э, фикс-пак 1: повторное приглашение сбрасывает права
                  человека (решение плана П3) — сказано до подтверждения. */}
              <p className="text-sm text-muted" data-testid="employees-reissue-rights-reset">
                {rightsRu.resetWarning.reinvite}
              </p>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="employees-reissue-cancel">Отмена</AlertDialogCancel>
              <AlertDialogAction
                data-testid="employees-reissue-confirm"
                disabled={reissueMutation.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  void onConfirm();
                }}
              >
                Перевыпустить
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          <div className="space-y-4" data-testid="employees-reissue-reveal">
            <AlertDialogHeader>
              <AlertDialogTitle>Новая ссылка-приглашение</AlertDialogTitle>
              <AlertDialogDescription data-testid="employees-reissue-reveal-desc">
                Передайте ссылку сотруднику. Срок действия — до{' '}
                {formatDate(reveal.expiresAt, 'dd.MM.yyyy HH:mm')}. Старая ссылка отозвана.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <TokenLinkDisplay
              url={reveal.inviteUrl}
              expiresAt={reveal.expiresAt}
              label="Ссылка-приглашение"
              testIdPrefix="employees-reissue-link"
            />
            <AlertDialogFooter>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="employees-reissue-close"
              >
                Закрыть
              </Button>
            </AlertDialogFooter>
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
